/**
 * The export slot — and there is deliberately no button in it.
 *
 * **Corrected by Task 44.** This file, and the two strings it renders, used to say
 * that `POST /api/v1/reports` was not mounted. That was true when Task 43 wrote it
 * and is false at `HEAD`: WP-K6 landed in `f332d28` and `src/server.ts` mounts
 * `createReportsRouter`. A UI telling a user that a feature does not exist when it
 * does is the same class of defect as a UI rendering an absence as a zero — it
 * survives review because the sentence used to be true.
 *
 * **The slot still ships no button, and the reason moved rather than disappeared.**
 * What is missing now is the screen at the other end: WP-K8 (Task 48) builds
 * Reports, and until it exists a press here would write a `wp_reports` row and
 * enqueue a generation job whose status **no screen in this build reads** and whose
 * file **no screen downloads** — `GET /reports/{id}` and `/download` are mounted and
 * nothing calls them. A control that starts work the person can never collect is
 * worse than no control, because the work is real. `POST /reports` also requires
 * `workforce.export_reports`, which is in no role's `DEFAULT_GRANTS`.
 *
 * Task 41 settled the shape of this situation for `POST /auth/switch-company` and
 * `web/shapes/select-company.md` §2 records the ruling: *state the position, do not
 * ship a control that cannot work.* A disabled button would still be worse than
 * either: it asserts "this could do something, but not for you", and the truth is
 * that the request would succeed and the result would be unreachable.
 *
 * So the slot names the request Reports will send, with the filters currently on
 * screen already in it. When Task 48 lands, the wiring is the `POST` this text
 * describes and nothing else on this screen changes.
 *
 * ── The body this slot printed was wrong, and it is corrected here ──────────
 *
 * **Found by the reports agent after WP-K6 (Task 46) landed, and fixed against
 * `ReportCreateRequest` in `openapi.yaml` rather than against what this file used to
 * say** — the file is the thing that was wrong, so it cannot be its own source.
 *
 * Five defects in one object literal, and every one of them would have been a
 * refusal rather than a wrong report, because the schema is `additionalProperties:
 * false` with four `required` members:
 *
 *  1. `type` is **`report_type`**. An unknown key is refused outright.
 *  2. There is no `params` wrapper. `period_start`, `period_end`, `department_id`
 *     and the rest are **top level**; a nested `params` is one more unknown key.
 *  3. `date_from` / `date_to` are **`period_start` / `period_end`**, and they are
 *     `format: date`, which is what this screen already holds.
 *  4. `employee_id` is **`employee_ids`**, an array (`maxItems: 1000`). A screen
 *     filtered to one person sends a one-element array; an unfiltered one sends the
 *     member **not at all**, because an empty array is a different request from an
 *     absent filter and only one of them means "everybody in scope".
 *  5. **`format` was missing entirely, and it is `required`.** `csv | xlsx | pdf` —
 *     `json` is not one of them: Task 46 removed it because `wp_reports.format`'s
 *     CHECK refuses it, and a format the constraint rejects is a `500` waiting for
 *     its first caller. This slot names `csv`, which is the attendance export's
 *     ordinary shape and the one Master §19 lists first.
 *
 * `attendance` is one of the **five of thirteen** report types WP-K6 implements, so
 * the request named here is one the server would actually accept; the other eight
 * are refused at `POST /reports` rather than producing an empty file.
 *
 * ── Two facts from Task 46 that this slot has to be right about ─────────────
 *
 * Neither changes the code here, and both are stated so the next person wiring the
 * button does not have to rediscover them:
 *
 *  - **A withheld value is never zero and never blank in the file.** CSV has no
 *    `null` and every spreadsheet reads an empty cell as `0`, so the generator emits
 *    `#NOT_COMPUTED`, `#WITHHELD_PRIVATE`, `#DATA_INCOMPLETE` and `#NOT_APPLICABLE`
 *    as **text**, inline strings in XLSX so `SUM()` skips them rather than adding a
 *    zero. Any screen that ever renders a report figure must render the sentinel as
 *    it arrived and must not coerce it to a number. **This slot renders no report
 *    figure at all** — it prints a request — so there is nothing here to coerce, and
 *    that is stated rather than left as an absence somebody reads as an oversight.
 *  - **`WP_REPORT_URL_TTL` is 180 seconds.** A download URL is a bearer credential
 *    that S3 does not re-check, and inside that window a revoked permission does not
 *    invalidate an already-issued link. A UI that holds one must expect expiry and
 *    call `/download` again — a request that *is* permission-checked — rather than
 *    presenting a dead link as a working one. **This slot holds no URL**, so there
 *    is nothing here to go stale.
 *
 * ── Why not a browser-side CSV in the meantime ──────────────────────────────
 *
 * The grid is already in memory, so one would be easy. It would also be a
 * **different artefact** from the one the plan means: WP-K6's file records
 * generated-by, generated-at and the filters used, has a `wp_reports` row behind it,
 * and is fetched through a scope-checked signed URL. A look-alike carrying none of
 * that would still be attached to emails and quoted in payroll disputes, and the
 * first person to notice the difference would be someone arguing about their pay.
 */

import type { ReactNode } from 'react';

import { useT } from '../../i18n';
import { StatusNote } from '../../states/StatusNote';

export interface ExportSlotProps {
  /** The window and filters on screen, rendered into the request that will be sent. */
  readonly params: {
    readonly date_from: string;
    readonly date_to: string;
    readonly employee_id: string | null;
    readonly department_id: string | null;
  };
}

/**
 * The report format this slot names. `csv | xlsx | pdf` are the only three
 * `wp_reports.format` accepts, and `format` is `required` — it was absent from the
 * body this file used to print.
 */
export const EXPORT_FORMAT = 'csv';

/**
 * `ReportCreateRequest`, built from the filters on screen.
 *
 * Exported so a test can assert its **shape** against the schema rather than against
 * a rendered string: a body checked only by reading the DOM is a body whose keys
 * nobody compared to the contract, which is exactly how the previous version shipped.
 *
 * An absent filter is an **omitted member**, never `null` and never `[]`: an empty
 * `employee_ids` is a different request from no `employee_ids`, and only the second
 * means "everybody in the caller's scope".
 */
export function exportRequestBody(params: ExportSlotProps['params']): Record<string, unknown> {
  return {
    report_type: 'attendance',
    format: EXPORT_FORMAT,
    period_start: params.date_from,
    period_end: params.date_to,
    ...(params.employee_id === null ? {} : { employee_ids: [params.employee_id] }),
    ...(params.department_id === null ? {} : { department_id: params.department_id }),
  };
}

export function ExportSlot({ params }: ExportSlotProps): ReactNode {
  const t = useT();

  return (
    <section className="wp-section" aria-labelledby="k3-export-heading">
      <h2 className="wp-section__title" id="k3-export-heading">
        {t('k3.export.heading')}
      </h2>
      <StatusNote severity="info" title={t('k3.export.unavailable')} testId="k3-export">
        {/* The window, in the reader's own words. The request body itself is a fact
            about the wire and lives in `exportRequestBody` above, where the schema
            test can read it — printing it here made a working screen look broken. */}
        <p className="wp-small" data-testid="k3-export-period">
          {t('k3.export.willSend', { from: params.date_from, to: params.date_to })}
        </p>
        <p className="wp-small wp-muted">{t('k3.export.noLocalFile')}</p>
      </StatusNote>
    </section>
  );
}
